import * as PropTypes from "prop-types";
import * as React from "react";
import { $, merge } from "glamor";
import Slider, { Settings } from "react-slick";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Theme } from "@material-ui/core";
import NoSwatch from "material-ui/svg-icons/image/texture";
import ChevronLeftIcon from "material-ui/svg-icons/navigation/chevron-left";
import ChevronRightIcon from "material-ui/svg-icons/navigation/chevron-right";
import { formatMessage } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import { IItemRecord } from "records/item/item";
import { ICart } from "components/page/site-header/cart";
import Label from "components/form/label";
import siteConfig from "site-config";
import ShoppingCartIcon from "components/icons/shopping-cart";
import { theme, rawTheme } from "styles/theme";
import "styles/slick/slick-theme.styl";
import "styles/slick/slick.styl";
import { getSwatch, getSwatchLabel, getBaseSwatchStyles, displayingNewColorways } from "helpers/swatch-utils";
import { ISwatch } from "constants/color-swatches";
import CartAdd from "components/icons/cart-add";
import CartCheck from "components/icons/cart-check";
import CartRemove from "components/icons/cart-remove";
import { DATATEST } from "constants/integration";
import { blurFix } from "styles/mixins";
import Banner from "../../items/item/banner";
import me from "models/me";

interface IProps {
    product: IItemRecord;
    scope: IScope;
    variants: IItemRecord[];
    onFetchProduct: (productId: string) => void;
    onAddAllColors?: () => void;
    onRemoveAllColors?: () => void;
    showLabel?: boolean;
    maxToShow: number; // max number of slides
    cart?: any;
    onPlaceOrder?: (variant: IItemRecord) => void;
    isItemInCart?: boolean;
    onHoverSwatch?: (anchor: HTMLElement, variant: IItemRecord, swatch: ISwatch) => void;
    onCloseSwatchZoom?: () => void;
}

interface ISlideProps {
    scope: IScope;
    label: string;
    variantId: string;
    swatch: ISwatch;
    imageUrl: string;
    currentProduct: boolean;
    onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    children?: React.ReactNode;
    onHoverSwatch?: (e: React.MouseEvent) => void;
    onCloseSwatchZoom?: () => void;
    showCart?: boolean;
    onPlaceOrder?: () => void;
    isItemInCart?: boolean;
    forceProductImage?: boolean;
    isNewProductPageEnabled?: boolean;
    isCustomizable?: boolean;
}

interface ISwatchProps {
    scope: IScope;
    label: string;
    variantId: string;
    swatch: ISwatch;
    currentProduct: boolean;
    onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    isItemInCart: boolean;
    newSwatches?: boolean;
    showCart?: boolean;
    imageUrl?: string;
    isNewProductPageEnabled?: boolean;
    onPlaceOrder: () => void;
    onHoverSwatch?: (e: React.MouseEvent) => void;
    onCloseSwatchZoom?: () => void;
    isCustomizable?: boolean;
}

interface ICartButtonProps {
    onCartMouseOver: () => void;
    onCartMouseLeave: () => void;
    className: string;
    onPlaceOrder: () => void;
    isItemInCart: boolean;
    cartHover: boolean;
    isCustomizable?: boolean;
}

interface IArrowProps {
    className?: string;
    style?: object;
    onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

interface IAddRemoveAllColors {
    cart: ICart;
    variants: IItemRecord[];
    onRemoveAllColors: () => void;
    onAddAllColors: () => void;
    scope: IScope;
}

export const NextArrow = (props: IArrowProps) => {
    const { className, style, onClick } = props;
    const handleClick = React.useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick?.(e);
        },
        [onClick],
    );
    return (
        <ChevronRightIcon
            data-test={DATATEST.GALLERY.CAROUSEL_ARROW}
            className={`${className} carouselIcon`}
            style={style}
            onClick={handleClick}
        />
    );
};

export const PrevArrow = (props: IArrowProps) => {
    const { className, style, onClick } = props;
    const handleClick = React.useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick?.(e);
        },
        [onClick],
    );
    return (
        <ChevronLeftIcon
            data-test={DATATEST.GALLERY.CAROUSEL_ARROW}
            className={`${className} carouselIcon`}
            style={style}
            onClick={handleClick}
        />
    );
};

export const Slide = (props: ISlideProps) => {
    const {
        label,
        swatch,
        scope,
        variantId,
        imageUrl,
        currentProduct,
        onClick,
        children,
        onHoverSwatch,
        onCloseSwatchZoom,
        showCart,
        onPlaceOrder,
        isItemInCart,
        forceProductImage = false,
        isNewProductPageEnabled = false,
        isCustomizable = false,
    } = props;
    const displaySwatch = scope.brand?.getPreference?.("display_swatch_for_colors") && !forceProductImage;
    const [cartHover, setCartHover] = React.useState<boolean>(false);

    const swatchStyles = displaySwatch ? getBaseSwatchStyles(swatch) : {};
    const classes = useStyles({ currentProduct, imageUrl });

    const handleCartMouseOver = React.useCallback(() => setCartHover(true), []);
    const handleCartMouseLeave = React.useCallback(() => setCartHover(false), []);
    const handlePlaceOrder = React.useCallback(() => {
        setCartHover(false);
        onPlaceOrder();
    }, [onPlaceOrder]);

    const noSwatch = displaySwatch && !swatchStyles;

    return (
        <div className={classes.slideContainer}>
            <div
                data-test={`color-variant-${variantId}`}
                key={variantId}
                className={classes.slide}
                onClick={onClick}
                data-id={variantId}
                onMouseEnter={onHoverSwatch}
                onMouseLeave={onCloseSwatchZoom}
            >
                {!noSwatch && (
                    <div className={classes.imageContainer}>
                        <div className={classes.slideImage} />
                        {isCustomizable && (
                            <Banner
                                item={null}
                                banner={{ bottom: true, background_color: "5354e1", foreground_color: "ffffff" }}
                                isColorWay={true}
                                scope={scope}
                            />
                        )}
                    </div>
                )}
                {noSwatch && <NoSwatch className={classes.noSwatch} />}
                {(!showCart || isNewProductPageEnabled) && (
                    <div className={classes.variantColorContainer}>
                        <div className={classes.variantColor}>{label}</div>
                    </div>
                )}
                {children}
            </div>
            {showCart && (
                <CartButton
                    onCartMouseOver={handleCartMouseOver}
                    onCartMouseLeave={handleCartMouseLeave}
                    className={classes.cart}
                    onPlaceOrder={handlePlaceOrder}
                    isItemInCart={isItemInCart}
                    cartHover={cartHover}
                    isCustomizable={isCustomizable}
                />
            )}
        </div>
    );
};

const AddRemoveAllColors = (props: IAddRemoveAllColors) => {
    const { variants = [], cart, scope, onRemoveAllColors, onAddAllColors } = props;

    const hasVariants = Boolean(variants.length);
    const isColorPreferenceEnabled = scope.brand.getPreference("new_colorway");
    const isColorWayFlagEnabled = siteConfig.NEW_COLORWAY_ENABLED === "true";

    if (!cart || !isColorWayFlagEnabled || !isColorPreferenceEnabled || !hasVariants) {
        return null;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const areAllVariantsInCart = React.useMemo(() => {
        const variantsInCart = variants.filter((variant) => cart.items.includes(variant._id));
        return variantsInCart.length === variants.length;
    }, [cart, variants]);

    return areAllVariantsInCart ? (
        <div className="flexItem-link" onClick={onRemoveAllColors}>
            <ShoppingCartIcon />
            <span className="addRemoveLabel" data-test={DATATEST.GALLERY.REMOVE_ALL_COLORS}>
                {formatMessage("Remove All Colors")}
            </span>
        </div>
    ) : (
        <div className="flexItem-link" onClick={onAddAllColors}>
            <ShoppingCartIcon />
            <span className="addRemoveLabel" data-test={DATATEST.GALLERY.ADD_ALL_COLORS}>
                {formatMessage("Add All Colors")}
            </span>
        </div>
    );
};

export const CartButton: React.FunctionComponent<ICartButtonProps> = ({
    onCartMouseOver,
    onCartMouseLeave,
    className,
    onPlaceOrder,
    isItemInCart,
    cartHover,
    isCustomizable = false,
}) => {
    return (
        <div
            onMouseOver={onCartMouseOver}
            onMouseLeave={onCartMouseLeave}
            className={className}
            onClick={onPlaceOrder}
            data-test={DATATEST.GALLERY.SWATCH_CART_ICON}
        >
            {isItemInCart && !isCustomizable && (cartHover ? <CartRemove /> : <CartCheck />)}
            {(isCustomizable || (!isCustomizable && !isItemInCart)) && (
                <CartAdd color={cartHover ? rawTheme.palette.primary1Color : undefined} />
            )}
        </div>
    );
};

export const Swatch = (props: ISwatchProps) => {
    const {
        scope,
        label,
        newSwatches = true,
        showCart = true,
        isNewProductPageEnabled = false,
        imageUrl = "",
        swatch,
        variantId,
        currentProduct,
        onClick,
        isItemInCart,
        onPlaceOrder,
        onHoverSwatch,
        onCloseSwatchZoom,
        isCustomizable = false,
    } = props;
    const [cartHover, setCartHover] = React.useState<boolean>(false);

    const hasNewColorway = scope.brand?.getPreference?.("new_colorway");
    const swatchStyles = getBaseSwatchStyles(swatch);

    const classes = useSwatchStyles({ currentProduct, swatchStyles, hasNewColorway, isNewProductPageEnabled });
    const classesLabel = useStyles({ currentProduct, imageUrl, swatchStyles });

    const handleCartMouseOver = React.useCallback(() => setCartHover(true), []);
    const handleCartMouseLeave = React.useCallback(() => setCartHover(false), []);
    const handlePlaceOrder = React.useCallback(() => {
        setCartHover(false);
        onPlaceOrder();
    }, [onPlaceOrder]);

    return (
        <div data-test={`color-variant-${variantId}`} key={variantId} className={classes.root} data-id={variantId}>
            <div
                className={classes.previewContainer}
                onMouseEnter={onHoverSwatch}
                onMouseLeave={onCloseSwatchZoom}
                data-test={DATATEST.GALLERY.SWATCH}
            >
                {swatchStyles ? (
                    <div className={classes.preview} onClick={onClick} />
                ) : (
                    <NoSwatch color={theme.palette.grey} className={classes.swatchNull} onClick={onClick} />
                )}
                {(isNewProductPageEnabled || !newSwatches) && (
                    <div className={classesLabel.variantColorContainer}>
                        <div className={classesLabel.variantColor}>{label}</div>
                    </div>
                )}
            </div>
            {newSwatches && showCart && (
                <CartButton
                    onCartMouseOver={handleCartMouseOver}
                    onCartMouseLeave={handleCartMouseLeave}
                    className={classes.cart}
                    onPlaceOrder={handlePlaceOrder}
                    isItemInCart={isItemInCart}
                    cartHover={cartHover}
                    isCustomizable={isCustomizable}
                />
            )}
        </div>
    );
};
export class Colors extends React.PureComponent<IProps, any> {
    static contextTypes = {
        muiTheme: PropTypes.object,
    };

    private styles = merge(
        $(" .singleColorBox", {
            position: "relative",
            paddingLeft: "20px",
            fontSize: "15px",
            lineHeight: "40px",
        }),
        $(" .flexContainer", {
            display: "flex",
            justifyContent: "space-between",
        }),
        $(" .flexItem", {
            marginTop: 5,
        }),
        $(" .flexItem-link", {
            cursor: "pointer",
            color: theme.palette.accent1Color,
        }),
        $(" .flexItem-link:hover svg", {
            filter: "invert(65%) sepia(53%) saturate(501%) hue-rotate(127deg) brightness(91%) contrast(100%)",
        }),
        $(" .addRemoveLabel", {
            margin: 5,
            verticalAlign: "super",
        }),
        $(" .label", {
            fontWeight: "bold",
            margin: "0px",
            display: "inline",
        }),
        $(" .variantCount", {
            color: theme.palette.canvasColor,
            backgroundColor: theme.palette.darkGrey,
            padding: "0 10px",
            borderRadius: "11px",
            lineHeight: "20px",
            marginLeft: "10px",
            fontSize: "12px",
        }),
        $(" .sliderContainer", {
            padding: "0 16px",
            maxWidth: this.props.variants.length < 6 ? `${this.props.variants.length * 100}px` : "100%",
        }),
        $(" .carouselIcon", {
            display: "block",
        }),
    );

    render() {
        const { product, scope, variants, maxToShow, cart, onPlaceOrder, onHoverSwatch, onCloseSwatchZoom } =
            this.props;

        if (!scope.brand.hasPreference("group_products") || !variants) {
            return null;
        }

        const newSwatches = displayingNewColorways(scope.brand);
        const displaySwatch = scope.brand?.getPreference?.("display_swatch_for_colors");
        const isNewProductPageEnabled = me.new_product_details;

        const canOrder = me.hasPermission("can_order");
        const showCart = canOrder && newSwatches;
        const variantSlides = [];
        let initialSlide = 0;

        variants.forEach((variant, idx) => {
            let isVariantInCart = false;
            const isVariantCustomizable = variant.hasCustomizationBanner(scope);

            if (variant && cart?.items) {
                isVariantInCart = cart?.items.has(variant._id);
            }
            const currentProduct = variant.get("_id") === product.get("_id");
            const handleClick = () => {
                return this.props.onFetchProduct(variant.get("_id"));
            };
            const handlePlaceOrder = () => onPlaceOrder(variant);

            const swatchObj = getSwatch(variant, scope);
            const handleHoverSwatch = (e: React.MouseEvent) =>
                onHoverSwatch?.(e.target as HTMLElement, variant, swatchObj);
            const handleCloseSwatchZoom = () => onCloseSwatchZoom?.();

            const imageUrl = variant.getImageUrl(scope, "T", 0);
            const swatch = displaySwatch ? (
                <Swatch
                    key={variant._id}
                    scope={scope}
                    label={getSwatchLabel(variant, scope)}
                    swatch={swatchObj}
                    variantId={variant.get("_id")}
                    currentProduct={currentProduct}
                    onClick={handleClick}
                    isItemInCart={isVariantInCart}
                    onPlaceOrder={handlePlaceOrder}
                    onHoverSwatch={handleHoverSwatch}
                    onCloseSwatchZoom={handleCloseSwatchZoom}
                    newSwatches={newSwatches}
                    showCart={canOrder}
                    isNewProductPageEnabled={isNewProductPageEnabled}
                    isCustomizable={isVariantCustomizable}
                />
            ) : (
                <Slide
                    key={variant._id}
                    scope={scope}
                    label={getSwatchLabel(variant, scope)}
                    swatch={swatchObj}
                    variantId={variant.get("_id")}
                    currentProduct={currentProduct}
                    imageUrl={imageUrl}
                    onClick={handleClick}
                    onHoverSwatch={handleHoverSwatch}
                    onCloseSwatchZoom={handleCloseSwatchZoom}
                    showCart={showCart}
                    onPlaceOrder={handlePlaceOrder}
                    isItemInCart={isVariantInCart}
                    isNewProductPageEnabled={isNewProductPageEnabled}
                    isCustomizable={isVariantCustomizable}
                />
            );
            if (currentProduct) {
                initialSlide = idx;
            }
            variantSlides.push(swatch);
        });

        const settings: Settings = {
            dots: false,
            lazyLoad: false,
            infinite: false,
            // initialSlide points on data-index, it's calculated for groups of slides (we use 2 per row)
            initialSlide: Math.max(Math.ceil((initialSlide + 1) / 2) - Math.min(maxToShow, variantSlides.length), 0),
            speed: 500,
            rows: 1,
            slidesPerRow: 2,
            slidesToShow: Math.min(maxToShow, variantSlides.length),
            slidesToScroll: Math.min(maxToShow, variantSlides.length),
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
            responsive: [
                {
                    breakpoint: 600,
                    settings: {
                        slidesToShow: Math.min(4, variantSlides.length),
                        slidesToScroll: Math.min(4, variantSlides.length),
                    },
                },
            ],
        };

        return (
            <div {...this.styles}>
                <div className="flexContainer">
                    <div className="flexItem">
                        <Label className="label">{formatMessage("Available Colors")}</Label>
                        <span className="variantCount" data-test={DATATEST.GALLERY.VARIANT_COUNT}>
                            {variants.length}
                        </span>
                    </div>
                    {canOrder && (
                        <AddRemoveAllColors
                            cart={cart}
                            variants={variants}
                            scope={scope}
                            onRemoveAllColors={this.props.onRemoveAllColors}
                            onAddAllColors={this.props.onAddAllColors}
                        />
                    )}
                </div>

                <div className="sliderContainer">
                    <Slider {...settings}>{variantSlides}</Slider>
                </div>
            </div>
        );
    }
}

const useSwatchStyles = makeStyles<
    Theme,
    { currentProduct: boolean; swatchStyles: any; hasNewColorway: boolean; isNewProductPageEnabled: boolean }
>({
    root: {
        padding: "4px",
        marginBottom: "8px",
        display: "flex",
        flexFlow: "column nowrap",
        alignItems: "center",
    },
    previewContainer: ({ currentProduct, hasNewColorway, isNewProductPageEnabled }) => {
        return {
            width: "64px",
            height: "64px",
            marginBottom: hasNewColorway ? (isNewProductPageEnabled ? "30px" : "auto") : "30px",
            padding: "2px",
            border: `2px solid ${currentProduct ? theme.palette.primary1Color : "transparent"}`,
            borderRadius: "6px",
            "&:hover": {
                borderColor: currentProduct ? theme.palette.primary1Color : theme.palette.grey,
            },
        };
    },
    preview: ({ swatchStyles, hasNewColorway }) => {
        return {
            borderRadius: "4px",
            width: "100%",
            height: "100%",
            cursor: "pointer",
            ...swatchStyles,
            backgroundRepeat: swatchStyles && !hasNewColorway ? "no-repeat" : "inherit",
        };
    },
    swatchNull: {
        width: "100% !important",
        height: "100% !important",
        cursor: "pointer",
    },
    cart: {
        margin: "8px",
        cursor: "pointer",
    },
});

const useStyles = makeStyles<Theme, { currentProduct: boolean; imageUrl: string; swatchStyles?: any }>({
    slideContainer: {
        display: "flex",
        flexFlow: "column nowrap",
        alignItems: "center",
    },
    slide: {
        cursor: "pointer",
    },
    slideImage: ({ currentProduct, imageUrl }) => {
        const slideImageStyle = {
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
            width: "65px",
            height: "102px",
            backgroundSize: "contain",
            border: "1px solid",
            borderColor: "transparent",
            borderRadius: "4px",
            ...blurFix,

            "&:hover": {
                borderColor: theme.palette.grey,
            },
        };
        const currentSelectedStyle = currentProduct
            ? {
                  borderColor: `${theme.palette.blue} !important`,
              }
            : {};
        const imageStyle = imageUrl
            ? { backgroundImage: `url(${imageUrl})` }
            : { backgroundColor: theme.palette.lightestGrey };
        return { ...slideImageStyle, ...currentSelectedStyle, ...imageStyle };
    },
    noSwatch: {
        width: "65px !important",
        height: "102px !important",
    },
    variantColor: ({ currentProduct }) => ({
        height: "34px",
        textAlign: "center",
        maxWidth: "65px",
        fontSize: "12px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordBreak: "break-word",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        color: currentProduct ? theme.palette.blue : "inherit",
    }),
    cart: {
        textAlign: "center",
        cursor: "pointer",
        paddingBottom: "10px",
    },
    imageContainer: {
        position: "relative",
        display: "inline-block",
    },
});
